import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Fish, Waves, Check, X, AlertTriangle, Lock, Eye, EyeOff } from 'lucide-react';

export default function AuthPage() {
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLocked, setIsLocked] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in — moved to useEffect to avoid setState during render
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  if (user) {
    return null;
  }

  // Password validation checks
  const pwChecks = {
    length: registerPassword.length >= 10,
    uppercase: /[A-Z]/.test(registerPassword),
    symbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(registerPassword),
    match: registerPassword.length > 0 && confirmPassword.length > 0 && registerPassword === confirmPassword,
  };
  const allPwValid = pwChecks.length && pwChecks.uppercase && pwChecks.symbol;
  const formValid = firstName.trim() && lastName.trim() && company.trim() && registerEmail && allPwValid && pwChecks.match && agreedToTerms;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
    const result = await login(loginEmail, loginPassword);
    if (result.success) {
      navigate('/');
    } else {
      setLoginError(result.detail || '');
      if (result.status === 403) {
        setIsLocked(true);
      }
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!formValid) return;
    setLoading(true);
    const result = await register(firstName.trim(), lastName.trim(), company.trim(), registerEmail, registerPassword);
    if (result.success || result === true) {
      navigate('/');
    }
    setLoading(false);
  };

  const PwCheck = ({ ok, label }) => (
    <div className={`flex items-center gap-1.5 text-xs ${ok ? 'text-green-600' : 'text-gray-400'}`}>
      {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      {label}
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-[#EBEBEB] dark:bg-[#0b1220] transition-colors duration-200">
      <div className="absolute inset-0 opacity-10">
        <Waves className="absolute top-10 left-10 w-32 h-32 text-[#0891b2] animate-pulse" />
        <Fish className="absolute bottom-20 right-20 w-24 h-24 text-[#0891b2] animate-bounce" />
        <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-[#0891b2] rounded-full filter blur-3xl opacity-10"></div>
        <div className="absolute bottom-1/3 left-1/4 w-80 h-80 bg-[#06748c] rounded-full filter blur-3xl opacity-10"></div>
      </div>
      
      <Card className="w-full max-w-md glass-card border-slate-200 relative z-10" data-testid="auth-card">
        <CardHeader className="space-y-3 text-center">
          <div className="flex justify-center mb-2">
            <div className="p-3 bg-[#0891b2] rounded-2xl">
              <Fish className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold text-[#0891b2]">
            Precision Fish Detection
          </CardTitle>
          <CardDescription className="text-gray-600">
            PFD Analysis - Advanced Fish Production Analytics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-slate-100">
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login" data-testid="login-form">
              <form onSubmit={handleLogin} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    data-testid="login-email-input"
                    type="email"
                    placeholder="your@email.com"
                    value={loginEmail}
                    onChange={(e) => { setLoginEmail(e.target.value); setLoginError(''); setIsLocked(false); }}
                    required
                    className="bg-white border-slate-300 focus:border-[#0891b2]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      data-testid="login-password-input"
                      type={showLoginPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={loginPassword}
                      onChange={(e) => { setLoginPassword(e.target.value); setLoginError(''); }}
                      required
                      disabled={isLocked}
                      className="bg-white border-slate-300 focus:border-[#0891b2] pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword((v) => !v)}
                      data-testid="toggle-login-password-visibility"
                      aria-label={showLoginPassword ? 'Hide password' : 'Show password'}
                      className="absolute inset-y-0 right-2 flex items-center text-slate-500 hover:text-slate-700 focus:outline-none"
                      tabIndex={-1}
                    >
                      {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {loginError && (
                  <div
                    data-testid="login-error-message"
                    className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
                      isLocked
                        ? 'bg-red-50 border border-red-200 text-red-700'
                        : 'bg-amber-50 border border-amber-200 text-amber-700'
                    }`}
                  >
                    {isLocked ? <Lock className="h-4 w-4 mt-0.5 shrink-0" /> : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />}
                    <span>{loginError}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="login-submit-button"
                  className="w-full bg-[#0891b2] hover:bg-[#06748c] text-white"
                  disabled={loading || isLocked}
                >
                  {loading ? 'Logging in...' : isLocked ? 'Account Locked' : 'Login'}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="register" data-testid="register-form">
              <form onSubmit={handleRegister} className="space-y-3 mt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="register-first-name">First Name *</Label>
                    <Input
                      id="register-first-name"
                      data-testid="register-first-name-input"
                      type="text"
                      placeholder="John"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      className="bg-white border-slate-300 focus:border-[#0891b2]"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="register-last-name">Last Name *</Label>
                    <Input
                      id="register-last-name"
                      data-testid="register-last-name-input"
                      type="text"
                      placeholder="Doe"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className="bg-white border-slate-300 focus:border-[#0891b2]"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="register-company">Company *</Label>
                  <Input
                    id="register-company"
                    data-testid="register-company-input"
                    type="text"
                    placeholder="Your company name"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    required
                    className="bg-white border-slate-300 focus:border-[#0891b2]"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="register-email">Email *</Label>
                  <Input
                    id="register-email"
                    data-testid="register-email-input"
                    type="email"
                    placeholder="your@email.com"
                    value={registerEmail}
                    onChange={(e) => setRegisterEmail(e.target.value)}
                    required
                    className="bg-white border-slate-300 focus:border-[#0891b2]"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="register-password">Password *</Label>
                  <div className="relative">
                    <Input
                      id="register-password"
                      data-testid="register-password-input"
                      type={showRegisterPassword ? 'text' : 'password'}
                      placeholder="Create a strong password"
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      required
                      className="bg-white border-slate-300 focus:border-[#0891b2] pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowRegisterPassword((v) => !v)}
                      data-testid="toggle-register-password-visibility"
                      aria-label={showRegisterPassword ? 'Hide password' : 'Show password'}
                      className="absolute inset-y-0 right-2 flex items-center text-slate-500 hover:text-slate-700 focus:outline-none"
                      tabIndex={-1}
                    >
                      {showRegisterPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {registerPassword.length > 0 && (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1.5">
                      <PwCheck ok={pwChecks.length} label="10+ characters" />
                      <PwCheck ok={pwChecks.uppercase} label="1 uppercase letter" />
                      <PwCheck ok={pwChecks.symbol} label="1 symbol (!@#$...)" />
                    </div>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="register-confirm-password">Confirm Password *</Label>
                  <div className="relative">
                    <Input
                      id="register-confirm-password"
                      data-testid="register-confirm-password-input"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Re-enter your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="bg-white border-slate-300 focus:border-[#0891b2] pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      data-testid="toggle-confirm-password-visibility"
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      className="absolute inset-y-0 right-2 flex items-center text-slate-500 hover:text-slate-700 focus:outline-none"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {confirmPassword.length > 0 && !pwChecks.match && (
                    <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                  )}
                  {pwChecks.match && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1"><Check className="h-3 w-3" /> Passwords match</p>
                  )}
                </div>

                <div className="flex items-start gap-2 pt-1">
                  <Checkbox
                    id="terms"
                    data-testid="register-terms-checkbox"
                    checked={agreedToTerms}
                    onCheckedChange={setAgreedToTerms}
                  />
                  <label htmlFor="terms" className="text-xs text-gray-600 leading-tight cursor-pointer">
                    I agree to the{' '}
                    <Link to="/data-security" target="_blank" className="text-[#0891b2] underline hover:text-[#06748c]">
                      Terms of Use & Data Security Policy
                    </Link>
                  </label>
                </div>

                <Button
                  type="submit"
                  data-testid="register-submit-button"
                  className="w-full bg-[#0891b2] hover:bg-[#06748c] text-white"
                  disabled={loading || !formValid}
                >
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
